import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Radio } from "lucide-react";
import GridCanvas from "./GridCanvas";
import KoView from "./KoView";

const LINES = [
  "Mission-Critical Technology,",
  "Defense Procurement &",
  "Technical Logistics Support",
];

const lineAnim = (i) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
});

export default function Hero() {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 120]);
  const opacity = useTransform(scrollY, [0, 600], [1, 0.2]);

  return (
    <section id="top" className="relative lg:min-h-[85vh] flex items-center overflow-hidden bg-[#0D0F12]" data-testid="hero-section">
      <motion.div style={{ y: yBg, opacity }} className="absolute inset-0">
        <GridCanvas />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0F12]/80 via-transparent to-[#0D0F12]" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-28 sm:pt-32 lg:pt-36 pb-14 lg:pb-20 w-full">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-mono text-[10px] sm:text-[11px] tracking-[0.25em] sm:tracking-[0.3em] uppercase text-amber-600 mb-5 sm:mb-6 flex items-center gap-3"
              data-testid="hero-eyebrow"
            >
              <span className="inline-block w-6 sm:w-8 h-px bg-amber-600 shrink-0" />
              DEFCOIN Ltd. — EU-Based Defense Contractor
            </motion.p>

            <h1 className="font-display font-bold uppercase tracking-tight text-[1.65rem] sm:text-[2rem] lg:text-[2.25rem] leading-snug text-slate-50 max-w-4xl" data-testid="hero-headline">
              {LINES.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span className="block" {...lineAnim(i)}>
                    {i === 1 ? (
                      <>
                        Defense <span className="text-[#8CA08F]">Procurement</span> &
                      </>
                    ) : i === 2 ? (
                      <>
                        Technical <span className="text-amber-600">Logistics</span> Support
                      </>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85 }}
              className="mt-6 max-w-2xl text-sm md:text-base text-slate-400 leading-relaxed"
              data-testid="hero-subheadline"
            >
              DEFCOIN Ltd. is an EU-based, NATO-CAGE registered government contractor delivering specialized IT
              systems integration, dual-use equipment sourcing, and defense supply chain coordination across
              European operational theaters (OCONUS).
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.05 }}
              className="mt-8 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4"
            >
              <a
                href="#capabilities"
                data-testid="hero-view-capabilities-btn"
                className="group flex items-center justify-center gap-3 min-h-[48px] bg-blue-600 hover:bg-[#55695A] text-white font-mono text-[11px] tracking-[0.15em] uppercase px-6 py-3 border border-blue-600 hover:border-amber-600 transition-colors duration-200"
              >
                View NAICS & Capabilities
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </a>
              <a
                href="#contact"
                data-testid="hero-contact-btn"
                className="flex items-center justify-center gap-3 min-h-[48px] text-slate-300 hover:text-amber-600 font-mono text-[11px] tracking-[0.15em] uppercase px-6 py-3 border border-slate-700 hover:border-amber-600 transition-colors duration-200"
              >
                <Radio className="w-4 h-4" />
                Contact Defense Desk
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="lg:col-span-5"
          >
            <KoView />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-10 lg:mt-16 grid grid-cols-2 md:grid-cols-4 border border-slate-800 bg-[#0D0F12]/70"
          data-testid="hero-stats-strip"
        >
          {[
            ["NCAGE", "01GUU"],
            ["UEI", "FJNKCGJV1QQ6"],
            ["OCONUS", "Eastern Flank"],
            ["24H", "RFQ Response"],
          ].map(([k, v], i) => (
            <div key={k} className={`px-4 sm:px-5 py-3.5 border-slate-800 ${i % 2 === 1 ? "border-l" : ""} ${i >= 2 ? "border-t md:border-t-0" : ""} ${i >= 2 ? "md:border-l" : ""}`}>
              <p className="font-mono text-xs text-amber-600 tracking-[0.15em]">{k}</p>
              <p className="font-mono text-[10px] text-slate-500 tracking-[0.2em] uppercase mt-1">{v}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
